import React from 'react';
import { Calendar, ExternalLink, AlertCircle, TrendingUp, Tag } from 'lucide-react';

const NewsCard = ({ news, featured = false }) => {
  const priorityColors = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const categoryColors = {
    hr: 'bg-blue-100 text-blue-700',
    payroll: 'bg-green-100 text-green-700',
    tax: 'bg-purple-100 text-purple-700',
    minimum_wage: 'bg-orange-100 text-orange-700',
    employment_act: 'bg-red-100 text-red-700',
    gazette: 'bg-indigo-100 text-indigo-700',
    general: 'bg-gray-100 text-gray-700'
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className={`
      bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow
      ${featured ? 'border-l-4 border-l-blue-500' : ''}
    `}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {/* Priority Badge */}
              {news?.priority && news?.priority !== 'low' && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                  priorityColors?.[news?.priority] || priorityColors?.low
                }`}>
                  <AlertCircle className="w-3 h-3" />
                  {news?.priority?.toUpperCase()}
                </span>
              )}

              {/* Category Badge */}
              {news?.category && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  categoryColors?.[news?.category] || categoryColors?.general
                }`}>
                  {news?.category?.replace('_', ' ')?.toUpperCase()}
                </span>
              )}

              {/* Featured Badge */}
              {featured && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                  ⭐ Featured
                </span>
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-blue-600 cursor-pointer">
              {news?.title}
            </h3>
          </div>
        </div>

        {/* Summary */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {news?.summary}
        </p>

        {/* Tags */}
        {news?.tags && news?.tags?.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Tag className="w-4 h-4 text-gray-400" />
            {news?.tags?.slice(0, 3)?.map((tag, index) => (
              <span key={index} className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(news?.publicationDate)}</span>
            </div>
            {news?.source && (
              <span className="text-xs">
                Source: {news?.source}
              </span>
            )}
          </div>

          {news?.sourceUrl && (
            <a
              href={news?.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
              title={`Visit ${news?.source} official website`}
            >
              Visit Website
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Source Information Notice */}
        {news?.sourceUrl && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
            <strong>Note:</strong> This links to the official {news?.source} website. For specific documents or detailed information, please contact the department directly or check their publications section.
          </div>
        )}

        {/* Effective Date Notice */}
        {news?.effectiveDate && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium text-amber-900">Effective Date: </span>
                <span className="text-amber-700">{formatDate(news?.effectiveDate)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Gazette Reference */}
        {news?.gazetteReference && (
          <div className="mt-3 text-xs text-gray-500">
            <span className="font-medium">Gazette Reference:</span> {news?.gazetteReference}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsCard;